
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger
} from '@/components/ui/sheet';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '@/contexts/LanguageContext';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleContactClick = () => {
    navigate('/contact');
    setIsOpen(false);
  };

  // Determine if current page has dark background
  // Modified to exclude contact page from dark background pages
  const isDarkBackground = 
    location.pathname === '/why-sell-to-noa';

  // Force scrolled appearance on contact page for better logo visibility
  const forceScrolled = location.pathname === '/contact';

  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || forceScrolled
          ? 'py-3 bg-noa-antiflash-white/90 backdrop-blur-md shadow-md' 
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/">
            <img 
              src={scrolled || forceScrolled ? "/lovable-uploads/211c873d-d0a6-4a75-8fea-a6e9926b1cc0.png" : "/lovable-uploads/662a87e6-f1ba-4f67-9522-3f96b2535b33.png"} 
              alt="Noa Partners Logo" 
              className="h-5 sm:h-6" 
            />
          </Link>
        </div>
        
        <div className="flex items-center space-x-6">
          {/* Language Toggle - moved to the right side of the navbar on mobile */}
          <div className={`${isMobile ? 'order-2 mr-2' : ''}`}>
            <LanguageToggle />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors ${
                scrolled || forceScrolled
                  ? 'text-noa-charcoal hover:text-noa-rich-black' 
                  : isDarkBackground
                    ? 'text-white hover:text-blue-300' 
                    : 'text-noa-charcoal hover:text-noa-rich-black'
              }`}
            >
              {t('home')}
            </Link>
            <Link 
              to="/why-sell-to-noa" 
              className={`text-sm font-medium transition-colors ${
                scrolled || forceScrolled
                  ? 'text-noa-charcoal hover:text-noa-rich-black' 
                  : isDarkBackground
                    ? 'text-white hover:text-blue-300' 
                    : 'text-noa-charcoal hover:text-noa-rich-black'
              } ${location.pathname === '/why-sell-to-noa' ? 'font-bold' : ''}`}
            >
              {t('partner_with_noa')}
            </Link>
            <Link 
              to="/about" 
              className={`text-sm font-medium transition-colors ${
                scrolled || forceScrolled
                  ? 'text-noa-charcoal hover:text-noa-rich-black' 
                  : isDarkBackground
                    ? 'text-white hover:text-blue-300' 
                    : 'text-noa-charcoal hover:text-noa-rich-black'
              } ${location.pathname === '/about' ? 'font-bold' : ''}`}
            >
              {t('about_noa')}
            </Link>
          </nav>
          
          {/* Contact Button */}
          <Button 
            onClick={handleContactClick}
            className={`transition-colors ${
              scrolled || forceScrolled || !isDarkBackground
                ? 'bg-noa-rich-black text-white hover:bg-noa-charcoal' 
                : 'bg-white text-noa-rich-black hover:bg-gray-200'
            } ${isMobile ? 'hidden' : ''}`}
          >
            {t('contact_us')}
          </Button>
          
          {/* Mobile Navigation Toggle */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button 
                size="icon" 
                variant="ghost" 
                className={`md:hidden ${isMobile ? 'order-1' : ''}`}
                aria-label="Toggle menu"
              >
                {isOpen ? 
                  <X className={`h-5 w-5 ${
                    scrolled || forceScrolled || !isDarkBackground 
                      ? 'text-noa-charcoal' 
                      : 'text-white'
                  }`} /> : 
                  <Menu className={`h-5 w-5 ${
                    scrolled || forceScrolled || !isDarkBackground 
                      ? 'text-noa-charcoal' 
                      : 'text-white'
                  }`} />
                }
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-3/4 sm:w-80 pt-16">
              <nav className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className={`text-lg font-medium py-2 transition-colors ${
                    location.pathname === '/' ? 'font-bold text-noa-rich-black' : 'text-noa-charcoal hover:text-noa-rich-black'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('home')}
                </Link>
                <Link 
                  to="/why-sell-to-noa" 
                  className={`text-lg font-medium py-2 transition-colors ${
                    location.pathname === '/why-sell-to-noa' ? 'font-bold text-noa-rich-black' : 'text-noa-charcoal hover:text-noa-rich-black'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('partner_with_noa')}
                </Link>
                <Link 
                  to="/about" 
                  className={`text-lg font-medium py-2 transition-colors ${
                    location.pathname === '/about' ? 'font-bold text-noa-rich-black' : 'text-noa-charcoal hover:text-noa-rich-black'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {t('about_noa')}
                </Link>
                
                {/* Mobile-only contact button in side menu */}
                <Button 
                  onClick={handleContactClick}
                  className="mt-4 w-full bg-noa-rich-black text-white hover:bg-noa-charcoal"
                >
                  {t('contact_us')}
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
